import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "./Header.js";
import Footer from "./Footer.js";
import Select from "react-select";
import { TailSpin } from "react-loader-spinner";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Style.css";
import GenerateGiftCard from "../../Components/Admin_components/generate_giftcard.js";
import Update_gift_card from "../../Components/Admin_components/update_gift_card.js";
import DeleteGiftCard from "../../Components/Admin_components/delete_gift_card.js";
import "../../Api/apiUrls";
import { getGiftCards } from "../../Api/apiUrls";

function GiftCard() {
  // Modal states
  const [modalAdd_newsIsOpen, setAdd_newsModalIsOpen] = useState(false);
  const [modalUpdateGiftCardIsOpen, setUpdateGiftCardModalIsOpen] = useState(false);
  const [modalDeleteGiftCardIsOpen, setDeleteGiftCardIsOpen] = useState(false);
  
  // Selected gift card details for modals
  const [selectedName, setSelectedName] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedRemark, setSelectedRemark] = useState(null);
  const [selectedSoldAt, setSelectedSoldAt] = useState(null);
  const [selectedMobileNumber, setSelectedMobileNumber] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedGstNumber, setSelectedGstNumber] = useState(null);
  const [selectedGiftCardId, setSelectedGiftCardId] = useState(null);

  // Filter and search states
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Data states
  const [allGiftCards, setAllGiftCards] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({});

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const observer = useRef();
  const searchInputRef = useRef();
  const tableContainerRef = useRef();

  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Redeem", label: "Redeem" },
  ];

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption.value);
    setPage(1);
  };

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
    // If search box is emptied, reload data immediately
    if (event.target.value === "") {
      setSearchTerm("");
      setPage(1);
    }
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    // Only update search term if it's different from current
    if (searchQuery !== searchTerm) {
      setSearchTerm(searchQuery);
      setPage(1);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const fetchGiftCards = useCallback(async (pageNum = 1, reset = false) => {
    setIsFetching(true);
    try {
      let url = `${getGiftCards}?page=${pageNum}&page_size=50`;
      
      if (selectedStatus !== "All") {
        url += `&status=${selectedStatus}`;
      }
      
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Network response was not ok");
      
      const data = await response.json();
      const newGiftCards = data.data.GiftCard || [];
      
      if (pageNum === 1 || reset) {
        setAllGiftCards(newGiftCards);
        setPagination(data.data.pagination || {});
        setTotalItems(data.data.pagination?.total_items || 0);
      } else {
        setAllGiftCards(prev => [...prev, ...newGiftCards]);
      }
      
      setHasMore(newGiftCards.length > 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  }, [selectedStatus, searchTerm]);

  useEffect(() => {
    fetchGiftCards(page);
  }, [page, fetchGiftCards]);

  // Handle scroll for infinite loading
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (
        !isFetching && 
        hasMore &&
        container.scrollHeight - container.scrollTop <= container.clientHeight + 100
      ) {
        setPage(prev => prev + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isFetching, hasMore]);

  // Reset and fetch when status or search term changes
  useEffect(() => {
    if (page === 1) {
      fetchGiftCards(1, true);
    } else {
      setPage(1);
    }
  }, [selectedStatus, searchTerm]);

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "--";
    const createdAtDate = new Date(createdAt);
    if (isNaN(createdAtDate)) return "--";
    
    const datePart = createdAtDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    
    const timePart = createdAtDate.toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
    
    return `${datePart} ${timePart}`;
  };


  const openAdd_news = () => {
    setAdd_newsModalIsOpen(true);
  };

  const closeAdd_news = () => {
    setAdd_newsModalIsOpen(false);
    fetchGiftCards(1, true);
    setPage(1);
  };

  const openUpdateGiftCard = (
    giftCardId,
    name,
    email,
    remark,
    sold_at,
    mobileNumber,
    address,
    gstNumber
  ) => {
    setSelectedGiftCardId(giftCardId);
    setSelectedName(name);
    setSelectedEmail(email);
    setSelectedRemark(remark);
    setSelectedSoldAt(sold_at);
    setSelectedMobileNumber(mobileNumber);
    setSelectedAddress(address);
    setSelectedGstNumber(gstNumber);
    setUpdateGiftCardModalIsOpen(true);
  };

  const closeUpdateGiftCard = () => {
    setUpdateGiftCardModalIsOpen(false);
    fetchGiftCards(1, true);
    setPage(1);
  };

  const openDeleteGiftCard = (giftCardId) => {
    setSelectedGiftCardId(giftCardId);
    setDeleteGiftCardIsOpen(true);
  };

  const closeDeleteGiftCard = () => {
    setDeleteGiftCardIsOpen(false);
    fetchGiftCards(1, true);
    setPage(1);
  };

  // Calculate showing count
  const showingCount = allGiftCards.length;
  const isFiltered = searchTerm || selectedStatus !== "All";

  return (
    <div>
      <Header />
      {modalAdd_newsIsOpen && <GenerateGiftCard onClose={closeAdd_news} />}

      <main id="main" className="main">
        <div className="pagetitle">
          <h1>GiftCard</h1>
        </div>
        <section className="section">
          <div className="card p-2">
            <div className="parent col-md-12 row mt-2">
              <div className="col-md-8 d-flex flex-wrap">
                <form onSubmit={handleSearchSubmit} className="d-flex">
                  <div className="child mr-2 mb-2">
                    <input
                      ref={searchInputRef}
                      onChange={handleSearchInputChange}
                      onKeyPress={handleKeyPress}
                      value={searchQuery}
                      type="text"
                      className="form-control"
                      placeholder="Search all fields..."
                    />
                  </div>
                  <div className="child mr-2 mb-2">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isFetching}
                    >
                      {isFetching ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </form>
                <div className="child mr-2 mb-2" style={{ minWidth: "150px" }}>
                  <Select
                    options={statusOptions}
                    placeholder="Status"
                    onChange={handleStatusChange}
                    value={statusOptions.find(option => option.value === selectedStatus)}
                    isDisabled={isFetching}
                  />
                </div>
              </div>

              <div className="col-md-4 text-right mb-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openAdd_news}
                  disabled={isFetching}
                >
                  <i className="bi bi-person-add"></i> Generate Gift Card
                </button>
              </div>
            </div>

            <div className="table-responsive" ref={tableContainerRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {isLoading ? (
                <div className="text-center my-5">
                  <TailSpin color="#0d6efd" width={50} />
                </div>
              ) : allGiftCards.length > 0 ? (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr.no</th>
                      <th>Splitted From</th>
                      <th>Pass</th>
                      <th>Card Number</th>
                      <th>Pass Code</th>
                      <th>Alloted to</th>
                      <th>Amount</th>
                      <th>Remaining Balance</th>
                      <th>Credits</th>
                      <th>Balance Credits</th>
                      <th>Sold At</th>
                      <th>Created On</th>
                      <th>Expiring On</th>
                      <th>Used By</th>
                      <th>Redeemed On</th>
                      <th>Status</th>
                      <th>Remark</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allGiftCards.map((giftCard, index) => (
                      <tr key={giftCard.id}>
                        <td>{index + 1}</td>
                        <td>
                          {giftCard.split_from_pass?.title || "---"}
                          <br />
                          <small>{giftCard.split_from_pass?.duration_type || ""}</small>
                        </td>
                        <td>
                          {giftCard.passes?.title || "---"}
                          <br />
                          <small>{giftCard.passes?.duration_type || ""}</small>
                        </td>
                        <td>{giftCard.card_code || "---"}</td>
                        <td>{giftCard.passcode || "---"}</td>
                        <td>
                          {giftCard.name || "---"}
                          <br />
                          <small>{giftCard.email || ""}</small>
                          <br />
                          <small>{giftCard.mobileNumber || ""}</small>
                        </td>
                        <td>{giftCard.amount || "0"}</td>
                        <td>{giftCard.remaining_balance || "0"}</td>
                        <td>{giftCard.credits || "0"}</td>
                        <td>{giftCard.balance_credits || "0"}</td>
                        <td>{giftCard.sold_at || "0"}</td>
                        <td>{formatCreatedAt(giftCard.created_at)}</td>
                        <td>{formatCreatedAt(giftCard.expiry_date)}</td>
                        <td>
                          {giftCard.used_by?.name || "-"}
                          <br />
                          <small>{giftCard.used_by?.email || ""}</small>
                        </td>
                        <td>{formatCreatedAt(giftCard.redeemed_at) || "-"}</td>
                        <td>
                          <span className={`badge ${giftCard.status === 'Active' ? 'bg-success' : 'bg-warning'}`}>
                            {giftCard.status || "-"}
                          </span>
                        </td>
                        <td>{giftCard.remark || "-"}</td>
                        <td>
                          <div className="d-flex">
                            {giftCard.status === 'Active' && (
                              <button 
                                className="btn btn-sm btn-outline-primary mr-1"
                                onClick={() => openUpdateGiftCard(
                                  giftCard.id,
                                  giftCard.name,
                                  giftCard.email,
                                  giftCard.remark,
                                  giftCard.sold_at,
                                  giftCard.mobileNumber,
                                  giftCard.address,
                                  giftCard.gst_number
                                )}
                                disabled={isFetching}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            )}

                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => openDeleteGiftCard(giftCard.id)}
                              disabled={isFetching}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-4">
                  <h4>No gift cards found</h4>
                  <p>Try adjusting your search or filters</p>
                </div>
              )}

              {isFetching && (
                <div className="text-center py-3">
                  <TailSpin color="#0d6efd" width={50} />
                </div>
              )}
            </div>

            <div className="p-3 bg-dark text-white rounded-bottom">
              Showing {showingCount} of {totalItems} total gift cards
              {hasMore && !isFetching && !isFiltered && " - scroll to load more"}
              {hasMore && isFiltered && (
                <button 
                  className="btn btn-sm btn-light ml-2"
                  onClick={() => {
                    const nextPage = page + 1;
                    if (pagination.total_pages && nextPage <= pagination.total_pages) {
                      setPage(nextPage);
                    }
                  }}
                  disabled={isFetching}
                >
                  Load More
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      {modalUpdateGiftCardIsOpen && (
        <Update_gift_card
          giftCardId={selectedGiftCardId}
          name={selectedName}
          email={selectedEmail}
          remark={selectedRemark}
          sold_at={selectedSoldAt}
          mobileNumber={selectedMobileNumber}
          address={selectedAddress}
          gst_number={selectedGstNumber}
          onClose={closeUpdateGiftCard}
        />
      )}

      {modalDeleteGiftCardIsOpen && (
        <DeleteGiftCard
          giftCardId={selectedGiftCardId}
          onClose={closeDeleteGiftCard}
        />
      )}

      <Footer />
    </div>
  );
}

export default GiftCard;